import { useIntl } from 'react-intl';
import { useSettingsStore } from '../stores/SettingsStore';
import useUser from './User';
import { CustomField } from '../interfaces/Settings';
import { useCustomeStore } from '../stores/CustomeStore';
import { usePipelineStore } from '../stores/PipelineStore';
import { useParams } from 'react-router-dom';
import { useMerchantStore } from '../stores/MerchantStore';
import { useCurrencyStore } from '../stores/CurrencyStore';
import { getCustomFieldFilteredMerchant, getCustomFieldFilteredPipeline, getCustomFieldFilteredUser } from '../functions/ExportTemplateExcel/functionHelperExcel';
import { useMeetingStore } from '../stores/MeetingsStore';

export const formatSubHeader = (field: any, customField: CustomField, intl: any) => {
  const fieldData = customField[field];
  const isRequired = fieldData.required;
  const type = fieldData.type;

  if (type === 'phone') {
    return isRequired ? `Ex: 628xxx` : `Ex: 628xxx (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`;
  }

  if (type === 'date') {
    return isRequired ? `Ex: 2024-12-30` : `Ex: 2024-12-30 (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`;
  }

  if (type === 'file' || type === 'image') {
    const title = 'URL';
    return isRequired
      ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title })}`
      : `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${title} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` })}`;
  }

  if (type === 'dateTimeRange') {
    return isRequired
      ? `Ex: 2024-11-30 12:11 - 2024-12-30 20:01`
      : `Ex: 2024-11-30 12:11 - 2024-12-30 20:01 (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`;
  }

  if (type === 'dateTime') {
    return isRequired
      ? `Ex: 2024-11-30 12:11`
      : `Ex: 2024-11-30 12:11 (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`;
  }

  if (type === 'time') {
    return isRequired
      ? `Ex: 13:50`
      : `Ex: 13:50 (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`;
  }

  const title = fieldData.name;
  return isRequired
    ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title })}`
    : `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${title} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` })}`;
};

const useNameColumnExcel = (source?: string) => {
  const { multiCurrencies } = useCurrencyStore()
  const { multiPipelines, selectMultiPipeline } = usePipelineStore()
  const { multiMerchants } = useMerchantStore()
  const { settings } = useSettingsStore();
  const { companyId } = useCustomeStore();
  const intl = useIntl();
  const user = useUser();
  const { templatePipelineId, templateMerchantId, templateMeetingId } = useParams()
  const { selectMultiMeeting } = useMeetingStore();
  const { multiMeetings } = useMeetingStore();

  const checkIsFeatureLabelEnabled = () => {
    if (source === 'template-meeting') {
      const multiTemplateMeeting = multiMeetings.find(meeting => meeting.id === templateMeetingId);
      return multiTemplateMeeting?.isFeatureLabelEnabled;
    } else if (source === 'template-pipeline') {
      const multiTemplatePipeline = multiPipelines.find(pipeline => pipeline.id === templatePipelineId);
      return multiTemplatePipeline?.isFeatureLabelEnabled;
    } else if (source === 'template-merchant') {
      const multiTemplateMerchant = multiMerchants.find(merchant => merchant.id === templateMerchantId);
      return multiTemplateMerchant?.isFeatureLabelEnabled;
    }
  }

  let arrayCustomFieldUsers: CustomField = settings.users_custom_fields ? JSON.parse(settings.users_custom_fields) : {};

  const filteredCustomFieldUsers = getCustomFieldFilteredUser(arrayCustomFieldUsers)
  const filteredCustomFieldMerchant = getCustomFieldFilteredMerchant(templateMerchantId as string, multiMerchants, user!)
  const filteredCustomFieldPipeline = getCustomFieldFilteredPipeline(templatePipelineId as string, multiPipelines, user!)

  var nameHeader = {
    export: {
      pipeline: {
        header: [
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_CREATED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
          user.data.company_id === companyId.elgisa || user.data.company_id === companyId.looyalIT ? 'Opportunity' : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.WHATSAPP' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")",
          ...(user?.data.company_name === "Maspion Square" ? [intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT' })] : []),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUM_PRODUCTS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_PRICE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_LAST_UPDATED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_LABEL' }),
        ],
        subHeader: [
          'Format (YYYY-MM-DD)',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          ...(user?.data.company_name === "Maspion Square" ? [''] : []),
          '',
          '',
          '',
          '',
          '',
          user.data.company_id === companyId.vedaPraxis ? 'Format (YYYY-MM-DD)' : 'Format (YYYY-MM-DD HH:mm)',
          user.data.company_id === companyId.vedaPraxis ? 'Format (YYYY-MM-DD)' : 'Format (YYYY-MM-DD HH:mm)',
          user.data.company_id === companyId.vedaPraxis ? 'Format (YYYY-MM-DD)' : 'Format (YYYY-MM-DD HH:mm:ss)',
          '',
          '',
        ]
      },
      log: {
        header: [
          settings.merchant_title,
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")",
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DURATION' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DESCRIPTION' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.FILE' }),
        ],
        subHeader: [
          '',
          '',
          '',
          '',
          '',
          '',
          'Format (YYYY-MM-DD HH:mm:ss)',
          '',
          '',
          '',
          '',
        ]
      },
      termin: {
        header: [
          'No',
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: 'Termin' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_DUE_TERMIN' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_PAID_TERMIN' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }),
          intl.formatMessage({ id: 'FORM.LABEL.PAYMENT_STATUS' }),
        ],
      },
      meeting: {
        header: [
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHOTO' }),
          settings.merchant_title,
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.STATUS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TYPE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PARTICIPANT' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LOCATION' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.REMINDER' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DURATION' }),
        ],
        subHeader: [
          '',
          '',
          '',
          '',
          '',
          'Format (YYYY-MM-DD HH:mm:ss)',
          'Format (YYYY-MM-DD HH:mm:ss)',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ]
      },
      merchant: {
        header: [
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.WHATSAPP' }),
          'Email',
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LAST_MEETING' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LAST_ADD_MERCHANT' }, { title: settings.pipeline_title }),
          user.data.company_id === companyId.yClinic ? intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PURCHASE_PACKAGE_COUNT' }) : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COUNT' }, { title: settings.pipeline_title }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COUNT' }, { title: settings.meeting_title }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
        ],
        subHeader: [
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          'Format (YYYY-MM-DD HH:mm:ss)',
        ]
      },
      reportLog: {
        header: [
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DURATION' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DESCRIPTION' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.FILE' }),
        ],
        subHeader: [
          '',
          'Format (YYYY-MM-DD HH:mm:ss)',
          '',
          '',
          '',
          '',
        ],
      },
      reportTermin: {
        header: [
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
          'Nominal Termin',
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_DUE_TERMIN' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_PAID_TERMIN' }),
        ],
        subHeader: [
          '',
          '',
          '',
          'Format (YYYY-MM-DD)',
          'Format (YYYY-MM-DD)',
        ]
      },
      reportProductsSold: {
        header: [
          `Database ${settings.pipeline_title}`,
          'Sales',
          settings.merchant_title,
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.INVOICE_DATE' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SKU' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_NAME' }),
          intl.formatMessage({ id: "FORM.LABEL.QUANTITY" }),
          intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.SALE_PRICE" }),
          intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.TOTAL_SALE" })
        ],
        subHeader: [
          '',
          '',
          '',
          '',
          'Format (DD/MM/YYYY)',
          '',
          '',
          '',
          '',
          '',
        ]
      },
      reimbursement: {
        header: [
          intl.formatMessage({ id: 'FORM.LABEL.DATE_SUBMITTED' }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
          intl.formatMessage({ id: "FORM.LABEL.CATEGORY" }),
          intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }),
          intl.formatMessage({ id: 'FORM.LABEL.USER' }),
          intl.formatMessage({ id: 'FORM.LABEL.STATUS' }),
        ],
        subHeader: [
          "Format (YYYY-MM-DD HH:mm:ss)",
          "",
          "",
          "",
          "",
          "",
        ]
      },
    },
    import: {
      insert: {
        pipeline: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
            settings.merchant_title,
            checkIsFeatureLabelEnabled() ? intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) : '',
            // intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")",
            // intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
            ...Object.keys(filteredCustomFieldPipeline).map((field: any) => filteredCustomFieldPipeline[field].name),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: settings.merchant_title }),
            checkIsFeatureLabelEnabled() ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` : '',
            // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) }) + " (" + selectMultiPipeline.currencyCode + ")",
            // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }) }),
            'Ex: 2024-12-30',
            `Ex: 2024-12-30 (${intl.formatMessage({ id: 'FORM.VALIDATION.SUB_HEADER_PROGRESS' })})`,
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }) }),
            ...Object.keys(filteredCustomFieldPipeline).map((field: any) => formatSubHeader(field, filteredCustomFieldPipeline, intl)),
          ],
        },
        merchant: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
            checkIsFeatureLabelEnabled() ? intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) : '',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
            'Email',
            ...Object.keys(filteredCustomFieldMerchant).map((field: any) => filteredCustomFieldMerchant[field].name),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }) }),
            checkIsFeatureLabelEnabled() ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` : '',
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }) }),
            'Ex: 628xxx',
            `Email (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
            ...Object.keys(filteredCustomFieldMerchant).map((field: any) => formatSubHeader(field, filteredCustomFieldMerchant, intl)),
          ],
        },
        user: {
          header: [
            'Username',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'FORM.LABEL.EMAIL' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ID_NUMBER' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PASSWORD' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.MANAGER' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ROLE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ATTENDANCE_TEMPLATE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_JOINED' }),
            ...Object.keys(filteredCustomFieldUsers).map((field: any) => filteredCustomFieldUsers[field].name),
          ],
          subHeader: [
            settings.custom_username_prefix ? intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.USERNAME' }) }) : `Ex: ${user?.data.company_prefix}-username / ${user?.data.company_prefix}_username`,
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }),
            `Ex: nama@email.com (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`, // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.EMAIL' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ID_NUMBER' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            'Ex: 628xxx',
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PASSWORD' })}` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.MANAGER' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' })}` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ROLE' })}` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ATTENDANCE_TEMPLATE' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            'Ex: 2024-12-30',
            ...Object.keys(filteredCustomFieldUsers).map((field: any) => formatSubHeader(field, filteredCustomFieldUsers, intl)),
          ],
        },
        unit: {
          header: [intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }), 'Alias'],
          subHeader: [
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }
            ),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'Alias' }),
          ],
        },
        product: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title }),
            intl.formatMessage({ id: 'FORM.LABEL.VAT' }),
            'SKU',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
          ],
          subHeader: [
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }
            ),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title }) }),
            // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PRICE' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.VAT' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'SKU' }),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              {
                title: `${intl.formatMessage({
                  id: 'MASTERS.DATATABLE.COLUMNS.NOTES',
                })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
              }
            ),
          ],
        },
        productUnit: {
          header: [
            'SKU',
            intl.formatMessage({ id: 'FORM.LABEL.UNITS' }),
            intl.formatMessage({ id: 'FORM.LABEL.UNIT_CONVERSION' }),
          ],
          subHeader: [
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              {
                title: intl.formatMessage(
                  { id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_TITLE' },
                  { title: 'SKU' }
                ),
              }
            ),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              { title: `${intl.formatMessage({ id: 'FORM.LABEL.UNITS' })}` }
            ) +
            '. Ex: gram (' +
            intl.formatMessage({ id: 'EXCEL.VALIDATION.BLANK_UNIT' }) +
            ')',
            'Ex: 10. 1 PCS = 10 gram (' + intl.formatMessage({ id: 'EXCEL.VALIDATION.FILL_UNIT' }) + ')',
          ],
        },
        productLabel: {
          header: ['SKU', intl.formatMessage({ id: 'FORM.LABEL.LABELS' })],
          subHeader: [
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              {
                title: intl.formatMessage(
                  { id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_TITLE' },
                  { title: 'SKU' }
                ),
              }
            ),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              { title: `${intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}` }
            ),
          ],
        },
        productPriceVariant: {
          header: [
            'SKU',
            intl.formatMessage({ id: 'PRICE_VARIANT' }),
            ...multiCurrencies.map(currency =>
              intl.formatMessage({ id: 'FORM.LABEL.PRICE' }) + ` (${currency.code})`
            ),
          ],
          subHeader: [
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              {
                title: intl.formatMessage(
                  { id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_TITLE' },
                  { title: 'SKU' }
                ),
              }
            ),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.CHOOSE' },
              { title: `${intl.formatMessage({ id: 'PRICE_VARIANT' })}` }
            ),
            ...multiCurrencies.map(currency =>
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PRICE' }) }) + ` (${currency.code})`,
            ),
          ],
        },
        meeting: {
          originalMeeting: {
            header: [
              intl.formatMessage({ id: 'FORM.LABEL.PIC' }),
              settings.merchant_title,
              checkIsFeatureLabelEnabled() ? intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) : '',
              intl.formatMessage({ id: 'FORM.LABEL.LOCATION' }),
              intl.formatMessage({ id: 'FORM.LABEL.NOTE' })
            ],
            subHeader: [
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PIC' }) }),
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: settings.merchant_title }),
              checkIsFeatureLabelEnabled() ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` : '',
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.LOCATION' }) }),
              `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.NOTE' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
            ]
          },
          scheduleMeeting: {
            header: [
              intl.formatMessage({ id: 'FORM.LABEL.DATE' }, { title: selectMultiMeeting?.name }),
              intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }),
              intl.formatMessage({ id: 'FORM.LABEL.REMINDER' })
            ],
            subHeader: [
              'Ex: 2024-12-30 atau Ex: 2024-11-30 12:11 - 2024-12-30 20:01',
              `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
              'Ex: 100 atau 1000 dalam satuan menit (optional)'
            ]
          },
          reportMeeting: {
            header: [
              intl.formatMessage({ id: 'FORM.LABEL.DATE' }, { title: selectMultiMeeting?.name }),
              intl.formatMessage({ id: 'FORM.LABEL.PHOTO' }, { title: selectMultiMeeting?.name }),
              intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }),
            ],
            subHeader: [
              'Ex: 2024-12-30 atau Ex: 2024-11-30 12:11 - 2024-12-30 20:01',
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PHOTO_URL' }) }),
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }) }),
            ]
          },
          visitMeeting: {
            header: [
              intl.formatMessage({ id: 'FORM.LABEL.PHOTO' }, { title: selectMultiMeeting?.name }),
            ],
            subHeader: [
              intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PHOTO_URL' }) }),
            ]
          },
          visitScheduleMeeting: {
            header: [
              intl.formatMessage({ id: 'FORM.LABEL.DATE' }, { title: selectMultiMeeting?.name }),
              // intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }),
              // intl.formatMessage({ id: 'FORM.LABEL.REMINDER' })
            ],
            subHeader: [
              'Ex: 2024-12-30 atau Ex: 2024-11-30 12:11 - 2024-12-30 20:01',
              // `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PARTICIPANT' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
              // 'Ex: 100 atau 1000 dalam satuan menit (optional)'
            ]
          }
        },
        productInPipeline: {
          header: [
            'Pipeline',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_NAME' }),
            user.data.is_allowed_access_hpp && selectMultiPipeline.isFeatureCogsEnabled ? 'HPP' : '',
            'Qty',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")",
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DISCOUNT' }) + " (" + selectMultiPipeline.currencyCode + ")",
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL' }),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: 'Pipeline' }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_NAME' }) }),
            user.data.is_allowed_access_hpp && selectMultiPipeline.isFeatureCogsEnabled ? intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `HPP (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }) : '',
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'Qty' }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")" }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DISCOUNT' }) + `(${selectMultiPipeline.currencyCode}) (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            intl.formatMessage({ id: 'FORM.LABEL.TOTAL_WITHOUT_PPN' }),
          ]
        }
      },
      update: {
        merchant: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CODE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
            checkIsFeatureLabelEnabled() ? intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) : '',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
            'Email',
            ...Object.keys(filteredCustomFieldMerchant).map((field: any) => filteredCustomFieldMerchant[field].name),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }) }),
            checkIsFeatureLabelEnabled() ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.LABELS' }) })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` : '',
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }) }),
            'Ex: 628xxx',
            `Email (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
            ...Object.keys(filteredCustomFieldMerchant).map((field: any) => formatSubHeader(field, filteredCustomFieldMerchant, intl)),
          ],
        },
        user: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CODE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'FORM.LABEL.EMAIL' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ID_NUMBER' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.MANAGER' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ROLE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ATTENDANCE_TEMPLATE' }),
            ...Object.keys(filteredCustomFieldUsers).map((field: any) => filteredCustomFieldUsers[field].name),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }),
            'Ex: nama@email.com', // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.EMAIL' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ID_NUMBER' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.MANAGER' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' })}` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ROLE' })}` }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.CHOOSE' }, { title: `${intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ATTENDANCE_TEMPLATE' })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` }),
            ...Object.keys(filteredCustomFieldUsers).map((field: any) => formatSubHeader(field, filteredCustomFieldUsers, intl)),
            // ...Object.keys(filteredCustomFieldUsers).map((field: any) =>
            //   filteredCustomFieldUsers[field].type === 'phone' && filteredCustomFieldUsers[field].required ? `Ex: 628xxx` : filteredCustomFieldUsers[field].type === 'phone' ? `Ex: 628xxx(${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`
            //     : filteredCustomFieldUsers[field].type === 'date' ? filteredCustomFieldUsers[field].required ? `Ex: 2024-12-30` : `Ex: 2024-12-30 (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`
            //       : filteredCustomFieldUsers[field].type === 'file' || filteredCustomFieldUsers[field].type === 'image' ? filteredCustomFieldUsers[field].required ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'URL' })}` : `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `URL (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` })}`
            //         : filteredCustomFieldUsers[field].required ? `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: filteredCustomFieldUsers[field].name })}` : `${intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: `${filteredCustomFieldUsers[field].name} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})` })}`
            // ),
          ]
        },
        unit: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CODE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            'Alias',
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }
            ),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'Alias' }),
          ],
        },
        product: {
          header: [
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CODE' }),
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title }),
            // intl.formatMessage({ id: 'FORM.LABEL.PRICE' }) + " (" + user?.data?.companyCurrency + ")",
            intl.formatMessage({ id: 'FORM.LABEL.VAT' }),
            'SKU',
            intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
            intl.formatMessage({ id: 'FORM.LABEL.COLOR_CODE' }),
          ],
          subHeader: [
            intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              { title: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }) }
            ),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title }) }),
            // intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PRICE' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: intl.formatMessage({ id: 'FORM.LABEL.VAT' }) }),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.ENTER' }, { title: 'SKU' }),
            intl.formatMessage(
              { id: 'EXCEL.VALIDATION.ENTER' },
              {
                title: `${intl.formatMessage({
                  id: 'MASTERS.DATATABLE.COLUMNS.NOTES',
                })} (${intl.formatMessage({ id: 'FORM.LABEL.OPTIONAL' })})`,
              }
            ),
            intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
          ],
        },
      },
      info: {
        fileInformation: intl.formatMessage({ id: 'EXCEL.LABEL.FILE_INFORMATION' }),
        downloader: intl.formatMessage({ id: 'EXCEL.LABEL.DOWNLOADER' }),
        downloadTime: intl.formatMessage({ id: 'EXCEL.LABEL.DOWNLOAD_TIME' }),
        type: intl.formatMessage({ id: 'EXCEL.LABEL.TYPE' }),
        warning: intl.formatMessage({ id: 'EXCEL.LABEL.WARNING' }),
        descWarning: intl.formatMessage({ id: 'EXCEL.LABEL.DESC_WARNING' }),
      },
      error: intl.formatMessage({ id: 'EXCEL.VALIDATION.IGNORE_COLUMN' }),
    },
  };

  return nameHeader;
};

export default useNameColumnExcel;
